/**
 * La zone d'un recadrage, en fractions de la photo (0 à 1), et ce que les gestes en font : la déplacer
 * d'un bloc, ou tirer l'un de ses coins. Pur : `<TutorCropFrame>` le lit à chaque mouvement du doigt ou
 * de la souris, les ports s'en servent pour la zone de départ et la découpe (`cropPixels`), et le test les
 * joue.
 *
 * Deux règles, celles de `ImageCropModal.web.js` (`react-image-crop`) :
 *  - la zone ne sort jamais de la photo : déplacée contre un bord, elle s'y arrête sans rapetisser ;
 *  - un coin tiré au-delà du coin opposé s'arrête à la taille minimale, il ne retourne pas la zone.
 */
import { type UITutorCropArea } from "../types/tutorEleves"

export type CropCorner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight"

/** La plus petite zone qu'un coin peut laisser, en fraction de chaque côté. */
export const MIN_CROP_FRACTION = 0.1

export const FULL_CROP_AREA: UITutorCropArea = { x: 0, y: 0, width: 1, height: 1 }

export const clamp = (value: number, min = 0, max = 1) => Math.max(min, Math.min(max, value))

/** La zone d'origine décalée de `dx`, `dy` (en fractions), retenue dans la photo. */
export function moveCropArea(start: UITutorCropArea, dx: number, dy: number): UITutorCropArea {
    return {
        ...start,
        x: clamp(start.x + dx, 0, 1 - start.width),
        y: clamp(start.y + dy, 0, 1 - start.height),
    }
}

/** La zone d'origine dont le coin `corner` a suivi `dx`, `dy` (en fractions) ; le coin opposé ne bouge pas. */
export function resizeCropArea(
    start: UITutorCropArea,
    corner: CropCorner,
    dx: number,
    dy: number,
    min: number = MIN_CROP_FRACTION
): UITutorCropArea {
    let left = start.x
    let top = start.y
    let right = start.x + start.width
    let bottom = start.y + start.height
    if (corner === "topLeft" || corner === "bottomLeft") left = clamp(left + dx, 0, right - min)
    else right = clamp(right + dx, left + min, 1)
    if (corner === "topLeft" || corner === "topRight") top = clamp(top + dy, 0, bottom - min)
    else bottom = clamp(bottom + dy, top + min, 1)
    return { x: left, y: top, width: right - left, height: bottom - top }
}

export type CropPixels = { x: number; y: number; width: number; height: number }

/**
 * La zone en pixels de la photo réelle, ce que la toile découpe : la formule de `createCroppedImage`
 * (`ImageCropModal.web.js`, la branche `%`), arrondie au pixel et retenue dans la photo.
 */
export function cropPixels(area: UITutorCropArea, naturalWidth: number, naturalHeight: number): CropPixels {
    const x = Math.max(0, Math.round(area.x * naturalWidth))
    const y = Math.max(0, Math.round(area.y * naturalHeight))
    const width = Math.max(1, Math.min(naturalWidth - x, Math.round(area.width * naturalWidth)))
    const height = Math.max(1, Math.min(naturalHeight - y, Math.round(area.height * naturalHeight)))
    return { x, y, width, height }
}
